import { MappingSchema } from '../mapping';

export class InventorySnapshotAthena {
  eventId: string;

  // Core Identification Fields
  objectId: string;
  logisticsUnitId: string;

  // Date and Time Fields
  snapshotTimestamp: Date;
  assignmentTimestampStart: Date;
  assignmentTimestampEnd: Date;
  receivedDate: Date;

  agingDate?: Date;
  expirationDate?: Date;

  lastMovementDate: Date;

  // Metadata
  consignee: string;
  sku: string;
  locationId: string;

  lot?: string;
  serialNumber?: string;

  // Quantity and UOM Fields
  quantity: number;
  uom?: string;
  locationUom?: string;
  inventoryStatus?: string;
  stockType?: string;

  // Min/Max and Control Fields
  minQty?: number;
  maxQty?: number;
  permanent?: boolean;

  // Cost and Valuation Fields
  unitCost?: number;
  totalValue?: number;

  // Temperature and Environmental
  temperatureZone?: string;
  temperatureReading?: string;

  // Allocation and Reservation
  allocatedQty?: number;
  availableQty?: number;
  onHoldQty?: number;

  // Cycle Count and Audit
  cycleCountDate?: Date;
  cycleCountVariance?: number;

  // ABC Classification
  abcClass?: string;

  // Movement Tracking
  lastMovementType?: string;
  lastMovementReference?: string;

  // Quality and Inspection
  qualityGrade?: string;
  quarantineReason?: string;

  // Supplier and Vendor Information
  supplier?: string;
  poNumber?: string;

  // Warehouse and Facility
  warehouse?: string;

  // Physical Characteristics
  weight?: number;
  volume?: number;

  // System Integration Fields
  sourceSystem?: string;
  externalId?: string;

  // Compliance and Regulatory
  complianceStatus?: string;

  // Inbound/Outbound Tracking
  inboundReference?: string;
  outboundReservation?: string;
}

export const INVENTORY_SNAPSHOT_MAPPING_SCHEMA: MappingSchema<InventorySnapshotAthena> =
  {
    title: 'Inventory Snapshot',
    description:
      'Complete inventory snapshot for WMS systems with historical tracking, billing, and analytics capabilities',

    fields: [
      {
        name: 'eventId',
        type: 'string',
        title: 'Event ID',
        description: 'Event',
        optional: false,
      },

      // Core Identification Fields
      {
        name: 'objectId',
        type: 'string',
        title: 'Unit ID',
        description: 'Id of a unit',
        optional: false,
      },

      {
        name: 'logisticsUnitId',
        type: 'string',
        title: 'Logistics Unit',
        description: 'Container/pallet/tote identifier (LPN, SSCC, etc.)',
        optional: false,
      },

      // Date and Time Fields
      {
        name: 'snapshotTimestamp',
        type: 'localDateTime',
        title: 'Snapshot Timestamp',
        description: 'When this inventory snapshot was taken',
        optional: false,
      },
      {
        name: 'assignmentTimestampStart',
        type: 'localDateTime',
        title: 'Start Assignment Date',
        description: 'Date when inventory was removed from location',
        optional: false,
      },
      {
        name: 'assignmentTimestampEnd',
        type: 'localDateTime',
        title: 'End Assignment Date',
        description: 'Date when inventory was removed from location',
        optional: false,
      },

      {
        name: 'receivedDate',
        type: 'localDateTime',
        title: 'Received Date',
        description: 'Date when inventory was received',
        optional: false,
      },

      {
        name: 'agingDate',
        type: 'localDateTime',
        title: 'Aging Date',
        description: 'Date for calculating inventory age',
        optional: true,
      },
      {
        name: 'expirationDate',
        type: 'localDateTime',
        title: 'Expiration Date',
        description: 'Product expiration date',
        optional: true,
      },
      {
        name: 'lastMovementDate',
        type: 'localDateTime',
        title: 'Last Movement Date',
        description: 'Date of last inventory movement',
        optional: false,
      },

      // Metadata
      {
        name: 'consignee',
        type: 'string',
        title: 'Client (Consignee)',
        description:
          'Client (consignee) — Name of your company or client (For 3PLs)',
        optional: false,
      },
      {
        name: 'sku',
        type: 'string',
        title: 'Item (SKU)',
        description: 'Item code (SKU)',
        optional: false,
      },
      {
        name: 'locationId',
        type: 'string',
        title: 'Location',
        description: 'Storage location identifier',
        optional: false,
      },
      {
        name: 'lot',
        type: 'string',
        title: 'Lot Number',
        description: 'Lot/batch number for traceability',
        optional: true,
      },
      {
        name: 'serialNumber',
        type: 'string',
        title: 'Serial Number',
        description: 'Unique serial number for individual item tracking',
        optional: true,
      },

      // Quantity and UOM Fields
      {
        name: 'quantity',
        type: 'integer',
        title: 'Quantity',
        description: 'Current stock quantity',
        optional: false,
      },
      {
        name: 'uom',
        type: 'string',
        title: 'UOM',
        description: 'Unit of Measure',
        optional: true,
      },
      {
        name: 'locationUom',
        type: 'string',
        title: 'Location UOM',
        description: 'Unit of measure at the location level',
        optional: true,
      },

      // Inventory Status and State
      {
        name: 'inventoryStatus',
        type: 'string',
        title: 'Inventory Status',
        description: 'Stock status (Available, Hold, Damaged, etc.)',
        optional: true,
      },
      {
        name: 'stockType',
        type: 'string',
        title: 'Stock Type',
        description:
          'Stock type classification (Unrestricted, QI, Blocked, etc.)',
        optional: true,
      },

      // Min/Max and Control Fields
      {
        name: 'minQty',
        type: 'integer',
        title: 'Minimum Quantity',
        description: 'Minimum stock level threshold',
        optional: true,
      },
      {
        name: 'maxQty',
        type: 'integer',
        title: 'Maximum Quantity',
        description: 'Maximum stock level threshold',
        optional: true,
      },
      {
        name: 'permanent',
        type: 'boolean',
        title: 'Permanent Stock',
        description: 'Indicates if this is permanent stock',
        optional: true,
      },

      // Cost and Valuation Fields
      {
        name: 'unitCost',
        type: 'number',
        title: 'Unit Cost',
        description: 'Cost per unit for valuation',
        optional: true,
      },
      {
        name: 'totalValue',
        type: 'number',
        title: 'Total Value',
        description: 'Total inventory value (quantity × unit cost)',
        optional: true,
      },

      // Temperature and Environmental
      {
        name: 'temperatureZone',
        type: 'string',
        title: 'Temperature Zone',
        description: 'Temperature control zone',
        optional: true,
      },
      {
        name: 'temperatureReading',
        type: 'number',
        title: 'Temperature Reading',
        description: 'Current temperature reading',
        optional: true,
      },

      // Allocation and Reservation
      {
        name: 'allocatedQty',
        type: 'integer',
        title: 'Allocated Quantity',
        description: 'Quantity allocated to orders',
        optional: true,
      },
      {
        name: 'availableQty',
        type: 'integer',
        title: 'Available Quantity',
        description: 'Quantity available for allocation',
        optional: true,
      },
      {
        name: 'onHoldQty',
        type: 'integer',
        title: 'On Hold Quantity',
        description: 'Quantity on hold/quarantine',
        optional: true,
      },

      // Cycle Count and Audit
      {
        name: 'cycleCountDate',
        type: 'localDateTime',
        title: 'Cycle Count Date',
        description: 'Date of last cycle count',
        optional: true,
      },
      {
        name: 'cycleCountVariance',
        type: 'number',
        title: 'Cycle Count Variance',
        description: 'Variance from last cycle count',
        optional: true,
      },

      // ABC Classification
      {
        name: 'abcClass',
        type: 'string',
        title: 'ABC Classification',
        description: 'ABC analysis classification',
        optional: true,
      },

      // Movement Tracking
      {
        name: 'lastMovementType',
        type: 'string',
        title: 'Last Movement Type',
        description: 'Type of last inventory movement',
        optional: true,
      },
      {
        name: 'lastMovementReference',
        type: 'string',
        title: 'Last Movement Reference',
        description: 'Reference number of last movement',
        optional: true,
      },

      // Quality and Inspection
      {
        name: 'qualityGrade',
        type: 'string',
        title: 'Quality Grade',
        description: 'Quality grade or classification',
        optional: true,
      },
      {
        name: 'quarantineReason',
        type: 'string',
        title: 'Quarantine Reason',
        description: 'Reason for quarantine if applicable',
        optional: true,
      },

      // Supplier and Vendor Information
      {
        name: 'supplier',
        type: 'string',
        title: 'Supplier',
        description: 'Supplier or vendor identifier',
        optional: true,
      },
      {
        name: 'poNumber',
        type: 'string',
        title: 'Purchase Order Number',
        description: 'Purchase order reference',
        optional: true,
      },

      // Warehouse and Facility
      {
        name: 'warehouse',
        type: 'string',
        title: 'Warehouse',
        description: 'Warehouse or facility identifier',
        optional: true,
      },

      // Physical Characteristics
      {
        name: 'weight',
        type: 'number',
        title: 'Weight',
        description: 'Total weight of inventory at this location',
        optional: true,
      },
      {
        name: 'volume',
        type: 'number',
        title: 'Volume',
        description: 'Total volume of inventory at this location',
        optional: true,
      },

      // System Integration Fields
      {
        name: 'sourceSystem',
        type: 'string',
        title: 'Source System',
        description: 'Source WMS system identifier',
        optional: true,
      },
      {
        name: 'externalId',
        type: 'string',
        title: 'External ID',
        description: 'External system identifier',
        optional: true,
      },

      // Compliance and Regulatory
      {
        name: 'complianceStatus',
        type: 'string',
        title: 'Compliance Status',
        description: 'Regulatory compliance status',
        optional: true,
      },

      // Inbound/Outbound Tracking
      {
        name: 'inboundReference',
        type: 'string',
        title: 'Inbound Reference',
        description: 'Reference to inbound shipment/receipt',
        optional: true,
      },
      {
        name: 'outboundReservation',
        type: 'string',
        title: 'Outbound Reservation',
        description: 'Outbound order allocation reference',
        optional: true,
      },
    ],
    // TODO define primary key
    // primaryKey: []
    appendable: true,
  };
